import { useEffect, useState, type FormEvent } from "react";
import { Loader2 } from "lucide-react";
import { request } from "../lib/api";
import type { CreatedResponse, ContentItemDetail, ContentType, Notice } from "../types";
import { CONTENT_TYPES, CONTENT_TYPE_LABELS, COMPLETION_RULES } from "../types";
import { ContentTypeFields, defaultContentFor } from "./ContentTypeFields";
import {
  Card,
  InlineNotice,
  errorMessage,
  inputClass,
  labelClass,
  primaryButtonClass,
  secondaryButtonClass,
  textareaClass,
} from "./ui";

// The editor for one content item: its type, title, media and completion
// rules. Shared by the content library and the PD day builder.
export interface ContentEditorProps {
  moduleID: string;
  itemID: string | null;
  onSaved: () => void;
  onCancel: () => void;
  onRequestDelete: (id: string) => void;
}

export function ContentEditor({
  moduleID,
  itemID,
  onSaved,
  onCancel,
  onRequestDelete,
}: ContentEditorProps) {
  const [type, setType] = useState<ContentType>("text");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [position, setPosition] = useState(1);
  const [required, setRequired] = useState(true);
  const [blockNext, setBlockNext] = useState(false);
  const [orderMode, setOrderMode] = useState("fixed");
  const [repeatable, setRepeatable] = useState(true);
  const [estimatedMinutes, setEstimatedMinutes] = useState<string>("");
  const [status, setStatus] = useState("draft");
  const [locale, setLocale] = useState("en");
  const [availabilityAt, setAvailabilityAt] = useState("");
  const [completionRule, setCompletionRule] = useState("viewed");
  const [completionThreshold, setCompletionThreshold] = useState<string>("");
  const [downloadAllowed, setDownloadAllowed] = useState(false);
  const [offlineAvailable, setOfflineAvailable] = useState(false);
  const [content, setContent] = useState<Record<string, unknown>>(defaultContentFor("text"));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<Notice | null>(null);

  useEffect(() => {
    if (!itemID) return;
    request<ContentItemDetail>(`/api/admin/content-items/${itemID}`)
      .then((item) => {
        setType(item.type);
        setTitle(item.title);
        setDescription(item.description || "");
        setPosition(item.position);
        setRequired(item.required);
        setBlockNext(item.block_next);
        setOrderMode(item.order_mode);
        setRepeatable(item.repeatable);
        setEstimatedMinutes(item.estimated_minutes ? String(item.estimated_minutes) : "");
        setStatus(item.status);
        setLocale(item.locale);
        setAvailabilityAt(item.availability_at ? item.availability_at.slice(0, 16) : "");
        setCompletionRule(item.completion_rule);
        setCompletionThreshold(item.completion_threshold ? String(item.completion_threshold) : "");
        setDownloadAllowed(item.download_allowed);
        setOfflineAvailable(item.offline_available);
        setContent(item.content || defaultContentFor(item.type));
      })
      .catch((cause) => setMessage({ tone: "error", text: errorMessage(cause) }));
  }, [itemID]);

  function changeType(next: ContentType) {
    setType(next);
    setContent(defaultContentFor(next));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!title.trim()) {
      setMessage({ tone: "error", text: "Title is required." });
      return;
    }
    setBusy(true);
    setMessage(null);
    const payload = {
      module_id: moduleID,
      type,
      title,
      description,
      position,
      required,
      block_next: blockNext,
      order_mode: orderMode,
      repeatable,
      estimated_minutes: estimatedMinutes ? Number(estimatedMinutes) : null,
      status,
      locale,
      availability_at: availabilityAt ? new Date(availabilityAt).toISOString() : null,
      completion_rule: completionRule,
      completion_threshold: completionThreshold ? Number(completionThreshold) : null,
      download_allowed: downloadAllowed,
      offline_available: offlineAvailable,
      content,
      translations: [],
    };
    try {
      if (itemID) {
        await request(`/api/admin/content-items/${itemID}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
      } else {
        await request<CreatedResponse>("/api/admin/content-items", {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }
      onSaved();
    } catch (cause) {
      setMessage({ tone: "error", text: errorMessage(cause) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      title={itemID ? "Edit content item" : "New content item"}
      text="Structured fields only — no JSON."
    >
      <form className="grid gap-5" onSubmit={submit}>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className={labelClass}>
            Content type
            <select
              className={inputClass}
              value={type}
              onChange={(e) => changeType(e.target.value as ContentType)}
              disabled={Boolean(itemID)}
            >
              {CONTENT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {CONTENT_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </label>
          <label className={labelClass}>
            Position
            <input
              type="number"
              min={1}
              className={inputClass}
              value={position}
              onChange={(e) => setPosition(Number(e.target.value))}
              required
            />
          </label>
        </div>
        <label className={labelClass}>
          Title
          <input
            className={inputClass}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </label>
        <label className={labelClass}>
          Description / instructions
          <textarea
            className={textareaClass}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>

        <fieldset className="grid gap-3 rounded-md border border-border bg-surface-muted p-4 sm:grid-cols-2">
          <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-content-secondary">
            Behaviour
          </legend>
          <label className="flex items-center gap-2 text-sm font-medium text-content-primary">
            <input
              type="checkbox"
              className="h-4 w-4 accent-interactive"
              checked={required}
              onChange={(e) => setRequired(e.target.checked)}
            />
            Required
          </label>
          <label className="flex items-center gap-2 text-sm font-medium text-content-primary">
            <input
              type="checkbox"
              className="h-4 w-4 accent-interactive"
              checked={blockNext}
              onChange={(e) => setBlockNext(e.target.checked)}
            />
            Block progression until completed
          </label>
          <label className="flex items-center gap-2 text-sm font-medium text-content-primary">
            <input
              type="checkbox"
              className="h-4 w-4 accent-interactive"
              checked={repeatable}
              onChange={(e) => setRepeatable(e.target.checked)}
            />
            Repeatable / revisitable
          </label>
          <label className={labelClass}>
            Order mode
            <select
              className={inputClass}
              value={orderMode}
              onChange={(e) => setOrderMode(e.target.value)}
            >
              <option value="fixed">Fixed</option>
              <option value="free">Flexible</option>
            </select>
          </label>
        </fieldset>

        <fieldset className="grid gap-3 rounded-md border border-border bg-surface-muted p-4 sm:grid-cols-2">
          <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-content-secondary">
            Completion & access
          </legend>
          <label className={labelClass}>
            Completion rule
            <select
              className={inputClass}
              value={completionRule}
              onChange={(e) => setCompletionRule(e.target.value)}
            >
              {COMPLETION_RULES.map((rule) => (
                <option key={rule} value={rule}>
                  {rule.replace(/_/g, " ")}
                </option>
              ))}
            </select>
          </label>
          <label className={labelClass}>
            Completion threshold (%, optional)
            <input
              type="number"
              min={0}
              max={100}
              className={inputClass}
              value={completionThreshold}
              onChange={(e) => setCompletionThreshold(e.target.value)}
            />
          </label>
          <label className={labelClass}>
            Status
            <select
              className={inputClass}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="hidden">Hidden</option>
            </select>
          </label>
          <label className={labelClass}>
            Language
            <select
              className={inputClass}
              value={locale}
              onChange={(e) => setLocale(e.target.value)}
            >
              <option value="en">English</option>
              <option value="ur">Urdu</option>
            </select>
          </label>
          <label className={labelClass}>
            Availability date (optional)
            <input
              type="datetime-local"
              className={inputClass}
              value={availabilityAt}
              onChange={(e) => setAvailabilityAt(e.target.value)}
            />
          </label>
          <label className={labelClass}>
            Estimated minutes
            <input
              type="number"
              min={0}
              className={inputClass}
              value={estimatedMinutes}
              onChange={(e) => setEstimatedMinutes(e.target.value)}
            />
          </label>
          <label className="flex items-center gap-2 text-sm font-medium text-content-primary">
            <input
              type="checkbox"
              className="h-4 w-4 accent-interactive"
              checked={downloadAllowed}
              onChange={(e) => setDownloadAllowed(e.target.checked)}
            />
            Download allowed
          </label>
          <label className="flex items-center gap-2 text-sm font-medium text-content-primary">
            <input
              type="checkbox"
              className="h-4 w-4 accent-interactive"
              checked={offlineAvailable}
              onChange={(e) => setOfflineAvailable(e.target.checked)}
            />
            Offline available
          </label>
        </fieldset>

        <div className="rounded-md border border-border p-4">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-content-secondary">
            {CONTENT_TYPE_LABELS[type]} details
          </p>
          <ContentTypeFields type={type} value={content} onChange={setContent} />
        </div>

        {message && <InlineNotice tone={message.tone} text={message.text} />}
        <div className="flex items-center gap-3">
          <button className={primaryButtonClass} disabled={busy}>
            {busy && <Loader2 size={16} className="animate-spin" />}
            {itemID ? "Save changes" : "Create item"}
          </button>
          <button type="button" className={secondaryButtonClass} onClick={onCancel}>
            Cancel
          </button>
          {itemID && (
            <button
              type="button"
              className="ml-auto text-sm font-semibold text-danger hover:underline"
              onClick={() => onRequestDelete(itemID)}
            >
              Delete item
            </button>
          )}
        </div>
      </form>
    </Card>
  );
}
